import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import * as Util from '../../../common/utilities/';
import * as StringUtils from '../../../utilities/stringUtils';

import {
  Card,
  Combobox,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';

import { AssetSummaryByClass, AssetSummaryBreakdown } from '../index';

import './AssetDistributionCard.scss';

class AssetDistributionCard extends Component {
  isPaging = false;

  ectTagID = 'ect_tags';
  ectTagLabel = this.props.intl.formatMessage({
    id: 'components.ecs.distribution.ect_tags'
  });

  assetTypeOptions() {
    return (this.props.assets.types || []).map(this.optionForType.bind(this));
  }

  optionForType(type) {
    return {
      id: type.type,
      label: type.type,
      value: type.type
    };
  }

  currentType() {
    const filter = this.props.assets.distributionFilter;
    return filter.type ? [this.optionForType({ type: filter.type })] : [];
  }

  onTypeSelect(evt, { selection }) {
    const filter = this.props.assets.distributionFilter;
    if (selection.length) {
      if (filter.query?.length) {
        delete filter.query;
      }
      this.updateSelectedType(selection[0].value);
    }
  }

  updateSelectedType(type) {
    this.props.actions.updateDistributionFilter({
      ...this.props.assets.distributionFilter,
      type
    });
  }

  currentTypeSummary() {
    const currentType = this.props.assets.distributionFilter.type;
    if (this.props.assets.distribution.type && currentType) {
      return this.props.assets.distribution.type[currentType];
    }
    return undefined;
  }

  assetAttributeOptions() {
    const options = (this.props.assets.keys || []).map(
      this.optionForAttribute.bind(this)
    );

    if (this.props.rules?.data?.rules?.settings?.ect) {
      const ectOption = {
        id: this.ectTagID,
        label: this.ectTagLabel,
        value: this.ectTagID
      };

      options.push(ectOption);
    }

    return options.sort(
      StringUtils.sortByField('label', this.props.intl.locale)
    );
  }

  optionForAttribute(attr) {
    const label = attr !== this.ectTagID ? attr : this.ectTagLabel;

    return {
      id: attr,
      label: label,
      value: attr
    };
  }

  currentAttribute() {
    const filter = this.props.assets.distributionFilter;
    return filter.attribute ? [this.optionForAttribute(filter.attribute)] : [];
  }

  onAttributeSelect(evt, { selection }) {
    const filter = this.props.assets.distributionFilter;
    if (selection.length) {
      if (filter.query?.length) {
        delete filter.query;
      }
      this.updateSelectedAttribute(selection[0].value);
    }
  }

  updateSelectedAttribute(attribute) {
    this.props.actions.updateDistributionFilter({
      ...this.props.assets.distributionFilter,
      attribute
    });
  }

  componentDidMount() {
    const userSkippedUpload = localStorage.getItem('userSkippedUpload');
    const skipToastWasClosed = localStorage.getItem('skipToastWasClosed');

    // skipToastWasClosed is checked to prevent appearance of skip upload notification multiple times after closing it once
    if (userSkippedUpload === 'true' && skipToastWasClosed !== 'true') {
      this.props.actions.openToast('skippedUpload');
    }
    this.bootstrapSelectedType();
  }

  bootstrapSelectedType() {
    // And we don't have a current selected type, but we have where to choose from - prevents crash on skipping upload
    if (!this.currentType().length && this.props.assets.types[0]) {
      // Select the first one
      this.updateSelectedType(this.props.assets.types[0].type);
    }
  }

  componentDidUpdate(prevProps) {
    // Types loaded
    if (Util.propChanged(prevProps, this.props, 'assets.types')) {
      this.bootstrapSelectedType();
    }

    // Keys loaded
    if (Util.propChanged(prevProps, this.props, 'assets.keys')) {
      // And we don't have a current selected key
      if (!this.currentAttribute().length) {
        // Select the first one
        this.updateSelectedAttribute(this.props.assets.keys[0]);
      }
    }

    // Filter changed, reload distribution
    if (Util.propChanged(prevProps, this.props, 'assets.distributionFilter')) {
      const {
        type,
        attribute,
        query,
        sortColumn
      } = this.props.assets.distributionFilter;
      if (type && attribute) {
        this.props.actions.getAssetDistributionByKey(
          attribute,
          { name: query, sortColumn },
          { page: 1 },
          { mode: 'reset' },
          { type }
        );
      }
    }

    if (Util.propChanged(prevProps, this.props, 'assets.distribution')) {
      this.isPaging = false;
    }
  }

  currentDistribution() {
    const { distributionFilter: filter, distribution } = this.props.assets;
    if (filter) {
      const { type, attribute } = filter;
      const key = `${attribute}${type}`;
      if (type && attribute && distribution[key]) {
        return distribution[key];
      }
    }
    return {};
  }

  currentPaging() {
    const { distributionFilter: filter, distribution } = this.props.assets;
    if (filter) {
      const { type, attribute } = filter;
      const key = `${attribute}${type}_paging`;
      if (type && attribute && distribution[key]) {
        return distribution[key];
      }
    }
  }

  hasMore() {
    return this.currentPaging()?.more;
  }

  handleLoadMore() {
    if (!this.isPaging) {
      this.isPaging = true;
      const currentPage = this.currentPaging().page;
      const {
        type,
        attribute,
        query,
        sortColumn
      } = this.props.assets.distributionFilter;
      if (type && attribute && currentPage) {
        this.props.actions.getAssetDistributionByKey(
          attribute,
          { name: query, sortColumn },
          { page: currentPage + 1 },
          { mode: 'combine' },
          { type }
        );
      }
    }
    this.isPaging = true;
  }

  handleSearch() {
    const {
      type,
      attribute,
      query,
      sortColumn
    } = this.props.assets.distributionFilter;
    if (type && attribute) {
      this.props.actions.getAssetDistributionByKey(
        attribute,
        { name: query, sortColumn },
        { page: 1 },
        { mode: 'reset' },
        { type }
      );
    }
  }

  toast() {
    const { formatMessage } = this.props.intl;
    if (this.props.toast.isOpen) {
      const documentationLink =
        'https://help.salesforce.com/articleView?id=mc_pb_einstein_content_selection.htm&type=5';

      const heading = (
        <FormattedMessage
          id="components.ecs.assetDistribution.skippedUploadToast.heading"
          values={{
            link: (
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={documentationLink}
              >
                {formatMessage({
                  id:
                    'components.ecs.assetDistribution.skippedUploadToast.headingLink'
                })}
              </a>
            )
          }}
        />
      );

      return (
        <Toast
          labels={{ heading }}
          variant="info"
          duration={10000}
          onRequestClose={() => {
            this.props.actions.closeToast('skippedUpload');
            localStorage.setItem('skipToastWasClosed', 'true');
          }}
        />
      );
    }

    return null;
  }

  render() {
    const { formatMessage } = this.props.intl;
    return (
      <React.Fragment>
        <Card
          heading={formatMessage({
            id: 'components.ecs.assetDistribution.header'
          })}
        >
          <div className="distribution-info">
            <FormattedMessage id="components.ecs.distribution.info" />
          </div>
          <div className="distribution-filter">
            <Combobox
              classNameContainer="distribution-combo"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.distribution.type'
                })
              }}
              options={this.assetTypeOptions()}
              variant="readonly"
              selection={this.currentType()}
              events={{
                onSelect: this.onTypeSelect.bind(this)
              }}
            />
            <Combobox
              classNameContainer="distribution-combo"
              labels={{
                label: formatMessage({
                  id: 'components.ecs.distribution.attribute'
                })
              }}
              options={this.assetAttributeOptions()}
              variant="readonly"
              selection={this.currentAttribute()}
              events={{
                onSelect: this.onAttributeSelect.bind(this)
              }}
            />
          </div>
          <AssetSummaryByClass summary={this.currentTypeSummary()} />
          <AssetSummaryBreakdown
            actions={this.props.actions}
            summary={this.currentTypeSummary()}
            distribution={this.currentDistribution()}
            filter={this.props.assets.distributionFilter}
            hasMore={this.hasMore()}
            onLoadMore={() => this.handleLoadMore()}
            onSearch={() => this.handleSearch()}
          />
          <ToastContainer>{this.toast()}</ToastContainer>
        </Card>
      </React.Fragment>
    );
  }
}

AssetDistributionCard.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.object,
  location: PropTypes.object,
  rules: PropTypes.object.isRequired,
  toast: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetDistributionCard);
